import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CandlestickData } from 'lightweight-charts';
import { getAssetDecimalPlaces } from '../utils/priceFormatter';

/**
 * Open / high / low / close and volume for the bar under the crosshair, falling back to the
 * live candle when the pointer is off the chart.
 *
 * Its inputs are refs rather than props with values, and it polls them, which is unusual
 * enough to explain: the chart deliberately keeps its candles out of React state so that a
 * tick — of which there are tens a second — cannot re-render the chart or disturb the
 * viewport. Reading through refs on a timer keeps that property while still letting this
 * small subtree update, so the cost of the readout is one render of these few spans rather
 * than one of the entire chart.
 */
interface ChartOhlcReadoutProps {
  symbol: string;
  candlesRef: React.MutableRefObject<CandlestickData[]>;
  volumesRef: React.MutableRefObject<Map<number, number>>;
  hoveredTimeRef: React.MutableRefObject<number | null>;
}

interface BarSnapshot {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number | null;
}

/**
 * Fast enough that moving the crosshair feels immediate, slow enough to be a rounding error
 * against the tick rate it is standing in for.
 */
const REFRESH_MS = 120;

/** Volume runs to millions on a liquid pair, so it is abbreviated rather than wrapped. */
const formatVolume = (volume: number): string => {
  if (!Number.isFinite(volume)) return '—';
  if (volume >= 1_000_000_000) return `${(volume / 1_000_000_000).toFixed(2)}B`;
  if (volume >= 1_000_000) return `${(volume / 1_000_000).toFixed(2)}M`;
  if (volume >= 1_000) return `${(volume / 1_000).toFixed(2)}K`;
  return volume.toFixed(2);
};

/**
 * The asset the volume is counted in. Binance reports a bar's volume in the base asset, so a
 * bare figure means one thing on BTCUSDT and something very different on SHIBUSDT; naming the
 * unit is what makes the two comparable at a glance.
 */
const QUOTE_ASSETS = ['USDT', 'USDC', 'BUSD', 'FDUSD', 'TUSD', 'USD', 'BTC', 'ETH', 'BNB'];

const baseAssetOf = (symbol: string): string => {
  const upper = symbol.toUpperCase();
  const quote = QUOTE_ASSETS.find(candidate => upper.endsWith(candidate) && upper.length > candidate.length);
  return quote ? upper.slice(0, -quote.length) : upper;
};

const sameBar = (a: BarSnapshot | null, b: BarSnapshot): boolean =>
  a !== null &&
  a.time === b.time &&
  a.open === b.open &&
  a.high === b.high &&
  a.low === b.low &&
  a.close === b.close &&
  a.volume === b.volume;

const ChartOhlcReadout: React.FC<ChartOhlcReadoutProps> = ({
  symbol,
  candlesRef,
  volumesRef,
  hoveredTimeRef
}) => {
  const [bar, setBar] = useState<BarSnapshot | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // The readout sits in the middle of the toolbar, so anything that changes its width shoves
  // the buttons to its right along with it — and on a moving market its width changes several
  // times a second. Tabular figures fix the common case, where only the digits change, and
  // this floor covers the rest: a price crossing into another digit, or a volume going from
  // "999.99K" to "1.00M". The readout is then only ever allowed to grow, which the eye does
  // not follow, rather than to twitch back and forth, which it cannot help following.
  const [widthFloor, setWidthFloor] = useState(0);

  // A different instrument prints different figures, so its floor is its own.
  useEffect(() => setWidthFloor(0), [symbol]);

  useLayoutEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const measured = node.scrollWidth;
    setWidthFloor(previous => (measured > previous ? measured : previous));
  });

  useEffect(() => {
    const read = () => {
      const candles = candlesRef.current;
      if (candles.length === 0) {
        setBar(previous => (previous === null ? previous : null));
        return;
      }

      const wanted = hoveredTimeRef.current;
      const candle =
        wanted === null
          ? candles[candles.length - 1]
          : candles.find(item => Number(item.time) === wanted) ?? candles[candles.length - 1];

      const next: BarSnapshot = {
        time: Number(candle.time),
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
        volume: volumesRef.current.get(Number(candle.time)) ?? null
      };

      // Returning the previous object lets React bail out, so a still market costs nothing.
      setBar(previous => (sameBar(previous, next) ? previous : next));
    };

    read();
    const timer = setInterval(read, REFRESH_MS);
    return () => clearInterval(timer);
  }, [candlesRef, volumesRef, hoveredTimeRef, symbol]);

  if (!bar) return null;

  const decimals = getAssetDecimalPlaces(symbol, bar.close);
  const isUp = bar.close >= bar.open;
  const valueColor = isUp ? '#2AB7A6' : '#ef4444';

  const field = (label: string, value: string) => (
    <span className="flex items-baseline gap-0.5">
      <span className="theme-text-quaternary">{label}</span>
      <span style={{ color: valueColor }}>{value}</span>
    </span>
  );

  return (
    <div
      ref={rootRef}
      className="flex items-center gap-2 px-2 py-1 text-[10px] font-medium rounded theme-bg-secondary border theme-border whitespace-nowrap tabular-nums"
      style={{ minWidth: widthFloor || undefined }}
    >
      {field('O', bar.open.toFixed(decimals))}
      {field('H', bar.high.toFixed(decimals))}
      {field('L', bar.low.toFixed(decimals))}
      {field('C', bar.close.toFixed(decimals))}
      <span className="flex items-baseline gap-0.5">
        <span className="theme-text-quaternary">V</span>
        <span className="theme-text-secondary">
          {bar.volume === null ? '—' : `${formatVolume(bar.volume)} ${baseAssetOf(symbol)}`}
        </span>
      </span>
    </div>
  );
};

export default ChartOhlcReadout;
